import { chunk, uniq } from 'es-toolkit';
import React, { useState } from 'react';
import ApprovalIcon from '@mui/icons-material/Approval';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HardwareIcon from '@mui/icons-material/Hardware';
import { CircularProgress } from '@mui/material';

import { Region, SharedComponentSet } from '@paragon/contract-library';
import {
  getClientUrls,
  hasEngineerNetworkAccessPermission,
  hasParagonBrandedShareEmailsPermission,
  hasProductionAccess,
  hasSupervisorFeatureFlag,
  UpgradeNotification,
} from '@paragon/login-tools';
import { enqueueSuccess } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';

import { downloadTrussDesignDrawingsForSharedComponentSet } from '../../../store/actions/downloadActions';
import { loadAnalysisSetByGuid } from '../../../store/actions/loadActions';
import { showUpgradeAccountDialog } from '../../../store/actions/popupActions';
import { getAnalysisSetsMap } from '../../../store/selectors/analysisSetsSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CreateJobForm } from './CreateJobForm';
import { DialogTitleWithBackButton } from './DialogTitleWithBackButton';
import { SendLinkForm } from './SendLinkForm';
import { ShareOption } from './ShareOption';

interface Props {
  readonly sharedComponentSet: SharedComponentSet;

  readonly regions: 'loading' | 'error' | readonly Region[];
  readonly visibleOrganizations: 'loading' | 'error' | readonly string[];

  readonly onNavigationBackRequested?: () => void;
}

export function SharingOperations({
  sharedComponentSet,
  regions,
  visibleOrganizations,
  onNavigationBackRequested,
}: Props) {
  const dispatch = useAppDispatch();

  const designUrlForEnvironment = useAppSelector(getClientUrls).designUrl;
  const sharedComponentSetLink = `${designUrlForEnvironment}${sharedComponentSet.lookupCode}`;
  const useParagonBrandedEmails = useAppSelector(hasParagonBrandedShareEmailsPermission);
  const hasEngineerNetworkAccess = useAppSelector(hasEngineerNetworkAccessPermission);
  const analysisSetsByGuid = useAppSelector(getAnalysisSetsMap);
  const userHasProductionAccess = useAppSelector(hasProductionAccess);
  const isProductionSupervisor = useAppSelector(hasSupervisorFeatureFlag);
  const productionUrlForEnvironment = useAppSelector(getClientUrls).productionUrl;

  const [sendLinkFormActive, setSendLinkFormActive] = useState<
    'normal' | 'fake-request-seals' | 'fake-request-repair' | false
  >(false);
  const [createJobFormActive, setCreateJobFormActive] = useState(false);
  const [loadingMissingAnalysisSets, setLoadingMissingAnalysisSets] = useState(false);

  const [downloadTddsInProgress, setDownloadTddsInProgress] = useState(false);

  // There is an edge case where a Designer Plan user might be viewing a Shared Component Set from a Free/Estimator Plan
  // user and choose to request seals/repairs. This is guarded against by checking that the analysis sets' check filter
  // is AllChecks. This is reliant on all the analysis sets being loaded, which is how we currently load Shared
  // Component Sets. If the user opens the share dialog before all analysis sets are loaded, it might be shown when it
  // shouldn't be (though it would be weird to have a Shared Component Set that is a mixture of AllChecks and not). Or,
  // if a Designer Plan user has selected one of their own Shared Component Sets that is not the actively loaded one on
  // the page, the analysis sets may not all be loaded, but it does not seem likely that a Designer Plan user would have
  // a Shared Component Set where not all are full analyses, unless maybe they upgraded from Estimator to Designer. All
  // in all, erring on the side of showing Request Seals/Repairs to those with the engineering network permission seemed
  // like the best approach. It should be fairly obvious to both the user and the sealing engineer that the trusses are
  // missing plates and plate checks.
  const isFitForRequestSealsOrRepairs = sharedComponentSet.analysisSetGuidsWithQuantities.every(
    ({ analysisSetGuid }) => {
      const analysisSet = analysisSetsByGuid.get(analysisSetGuid);
      if (!analysisSet) return true; // Err on the side of making request seals/repairs available
      return analysisSet.checkFilter === 'AllChecks';
    },
  );

  async function onClickRequestSealOrRepair() {
    setCreateJobFormActive(true);

    // Load any missing analysis sets so that we know if any of the  analyses have errors or are over capacity
    const missingAnalysisSetGuids = uniq(
      sharedComponentSet.analysisSetGuidsWithQuantities.map(({ analysisSetGuid }) => analysisSetGuid),
    ).filter((analysisSetGuid) => !analysisSetsByGuid.has(analysisSetGuid));
    if (missingAnalysisSetGuids) {
      setLoadingMissingAnalysisSets(true);
      const batchedMissingAnalysisSetGuids = chunk(missingAnalysisSetGuids, 10);
      for (const batch of batchedMissingAnalysisSetGuids) {
        await Promise.all(
          batch.map((analysisSetGuid) =>
            dispatch(
              loadAnalysisSetByGuid(analysisSetGuid, {
                setAnalysisSetAsActive: false,
                skipSettings: true,
                forceUpdateWorkingChanges: false,
                skipJackGirderConnections: true,
              }),
            ),
          ),
        );
      }
      setLoadingMissingAnalysisSets(false);
    }
  }

  if (
    sendLinkFormActive ||
    // FIXME: this could be polished to work better if loading is slow
    (createJobFormActive && Array.isArray(visibleOrganizations) && visibleOrganizations.length === 0)
  )
    return (
      <SendLinkForm
        sharedComponentSet={sharedComponentSet}
        titleMode={
          sendLinkFormActive === 'fake-request-seals'
            ? 'seal'
            : sendLinkFormActive === 'fake-request-repair'
              ? 'repair'
              : 'normal'
        }
        onNavigationBackRequested={() => {
          setSendLinkFormActive(false);
          setCreateJobFormActive(false);
        }}
      />
    );

  if (createJobFormActive) {
    if (loadingMissingAnalysisSets)
      return (
        <Dialog insistent>
          <CircularProgress />
        </Dialog>
      );
    else
      return (
        <CreateJobForm
          regions={regions}
          visibleOrganizations={visibleOrganizations}
          onNavigationBackRequested={() => setCreateJobFormActive(false)}
          onSendEmailRequested={() => setSendLinkFormActive('fake-request-seals')}
          sharedComponentSet={sharedComponentSet}
        />
      );
  }

  return (
    <Dialog
      variant="design"
      hasTransition={false}
      onCancel="close"
      title={
        onNavigationBackRequested ? (
          <DialogTitleWithBackButton
            title={`Share ${sharedComponentSet.name}`}
            onBackClick={onNavigationBackRequested}
          />
        ) : (
          `Share ${sharedComponentSet.name}`
        )
      }
      contentSx={[{ display: 'flex', flexDirection: 'column', gap: 2 }, createScrollbarStyling]}
    >
      <UpgradeNotification
        active={!hasEngineerNetworkAccess}
        description="Upgrade to the Designer Plan to send seal and repair requests to our engineering network."
        trialDescription="Start your free trial to send seal and repair requests to our engineering network."
        onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
      />

      <ShareOption
        icon={<ContentCopyIcon />}
        buttonText="Copy Link"
        fullText="Get a link to view these components in Design"
        disabled={downloadTddsInProgress}
        onClick={() => {
          dispatch(closeCurrentPopup());
          (navigator as any).clipboard
            .writeText(sharedComponentSetLink)
            .then(() => dispatch(enqueueSuccess('Copied to clipboard!')));
        }}
      />

      <ShareOption
        icon={<FileDownloadIcon />}
        buttonText={downloadTddsInProgress ? 'Downloading...' : 'Download'}
        fullText="Download design drawings with smart QR codes"
        disabled={downloadTddsInProgress}
        onClick={async () => {
          if (!sharedComponentSet) throw new Error('Clicked Download without specified SharedComponentSet');

          setDownloadTddsInProgress(true);
          await dispatch(downloadTrussDesignDrawingsForSharedComponentSet(sharedComponentSet));
          dispatch(closeCurrentPopup());
        }}
      />

      <ShareOption
        icon={<EmailIcon />}
        buttonText="Email Link"
        fullText="Send someone a link to view these components"
        disabled={downloadTddsInProgress}
        onClick={() => {
          if (useParagonBrandedEmails) {
            setSendLinkFormActive('normal');
            return;
          }

          window.open(
            `mailto:?subject=Shared Trusses: ${sharedComponentSet.name}&body=View shared trusses on Paragon at ${designUrlForEnvironment}${sharedComponentSet.lookupCode}`,
            '_blank',
          );
          dispatch(closeCurrentPopup());
        }}
      />

      {hasEngineerNetworkAccess && isFitForRequestSealsOrRepairs && (
        <ShareOption
          icon={<ApprovalIcon />}
          buttonText="Request Seal/Repair"
          fullText="Ask a professional engineer to repair or seal these components"
          disabled={downloadTddsInProgress}
          onClick={onClickRequestSealOrRepair}
        />
      )}

      {isProductionSupervisor && userHasProductionAccess && (
        <ShareOption
          icon={<HardwareIcon />}
          buttonText="Make Assembly Group"
          fullText="Make assembly group(s) for these components"
          onClick={() =>
            void window.open(
              `${productionUrlForEnvironment}assembly-groups?startCreateAssemblyGroupForLookupCode=${sharedComponentSet.lookupCode}`,
              '_blank',
            )
          }
        />
      )}
    </Dialog>
  );
}
